/**
 * Inline prompt directives — `[opus] fix x`, `[cc-opus, 50] …`, `[model:openai/gpt-5.5] …` — pure,
 * dependency-free. A message may open with one or more `[..]` groups that pick the model (and, for
 * hosts that honour them, turns/thinking/effort/engine/resume); the rest is the prompt.
 *
 * Shared by every agentx host (unclaw/Shraga, Bare) so the grammar and its edge cases live once:
 * multi-group, strict mode (bracketed prose stays prose), and an unresolvable EXPLICIT model is
 * reported (`unresolvedModel`) rather than dropped — dropping it ran the turn on the previous model.
 *
 * Model tokens resolve through the alias table and an injected `ModelResolver` (the host's catalog,
 * see `makeModelResolver`). `<vendor>-<model>` / `<vendor>/<model>` shorthands (`cc-opus` →
 * `claude-code/opus`, see `VENDOR_SHORTHAND`) select a model only when they resolve (`[cc-by] …` stays prose).
 */
import { MODEL_ALIASES } from './models';

export interface Directives {
    model?: string;
    turns?: number;
    thinking?: 'adaptive' | 'enabled' | 'disabled';
    effort?: 'low' | 'medium' | 'high' | 'max';
    engine?: string;
    /** Session resume for this conversation (host-defined). */
    resume?: boolean;
    /** DELEGATE mode `[planner->worker]` (key `delegate`): the worker model; `model` (the left side) plans. */
    delegate?: string;
    /** ADVISOR mode `[worker<-advisor]` (key `advisor`): the advisor model; `model` (the left side) works. */
    advisor?: string;
    /** `[off]` / `[mode:off]` (key `mode`): clear a sticky delegate/advisor mode. */
    modeOff?: boolean;
}

export interface ParsedPrompt {
    prompt: string;
    directives: Directives;
    /** An EXPLICIT model selection nothing could resolve. The caller must fail the turn, never run the old model. */
    unresolvedModel?: string;
    /** When `unresolvedModel` matched several catalog ids: the candidates (use the full id). */
    ambiguous?: string[];
}

export type ModelHit = { model: string; engine?: string };
/** Resolves a model token against a host catalog. `{ ambiguous }` = several ids match; never guess. */
export type ModelResolver = (token: string) => ModelHit | { ambiguous: string[] } | null;

/** Vendor shorthand → provider id: `[cc-opus]` / `[cc/opus]` = `claude-code/opus`. Extend per host via options. */
export const VENDOR_SHORTHAND: Record<string, string> = { cc: 'claude-code', cur: 'cursor', oa: 'openai' };

/** Every key agentx understands. A host passing its own `keys` must list `delegate` / `advisor` / `mode` to honour
 *  `[a->b]` / `[a<-b]` / `[off]` (and their keyed forms `delegate:x`, `advisor:x`, `mode:off`). */
export const DIRECTIVE_KEYS = ['model', 'turns', 'thinking', 'think', 'effort', 'engine', 'resume', 'delegate', 'advisor', 'mode'];

/** `a->b` (delegate) / `a<-b` (advisor): the left side may be empty (keep the current model). */
export function splitModePair(token: string): { left: string; op: '->' | '<-'; right: string } | null {
    const i = token.search(/->|<-/);
    if (i === -1) return null;
    const right = token.slice(i + 2).trim();
    return right ? { left: token.slice(0, i).trim(), op: token.slice(i, i + 2) as '->' | '<-', right } : null;
}
const isModel = (t: string, p: DirectiveOptions) => { const h = resolveModelToken(t, p); return !!h && 'model' in h; };

export interface DirectiveOptions {
    /** Host catalog lookup. Default: the one set by `setModelResolver` (none). */
    resolveModel: ModelResolver | null;
    /** Try the catalog before the alias table (Bare: `[opus]` = the catalog's `claude-code/opus`). */
    resolverFirst: boolean;
    aliases: Record<string, string>;
    vendors: Record<string, string>;
    /** Keys this host honours; positional turns/thinking count only when `turns`/`thinking` are listed. */
    keys: string[];
    /** Every group, the first included, must look like directives (else it stays prompt text). */
    strict: boolean;
    /** Resolve to catalog ids ONLY (Bare): no alias-table fallback, a full id the catalog lacks is refused, and a
     *  bare word matches an id's tail only when it is a known alias or carries a digit (`[auto]` stays prose). */
    catalogOnly: boolean;
    warn: (msg: string) => void;
}

let defaultResolver: ModelResolver | null = null;
/** Process-wide default resolver (Shraga's engine registry injects it once). Per-call options override. */
export function setModelResolver(fn: ModelResolver | null): void { defaultResolver = fn; }

const opt = (o?: Partial<DirectiveOptions>): DirectiveOptions => ({
    resolveModel: defaultResolver, resolverFirst: false, aliases: MODEL_ALIASES, vendors: VENDOR_SHORTHAND,
    keys: DIRECTIVE_KEYS, strict: false, catalogOnly: false, warn: (m) => console.warn(m), ...o,
});

// A group ends at whitespace or the end: `[opus](http://x)` is a markdown link, not a directive.
const DIRECTIVE_RE = /^\s*\[([^\]]*)\](?=\s|$)\s*([\s\S]*)/;
const isQualifiedModel = (v: string) => /^[a-z0-9._-]+\/[a-z0-9./_-]+$/.test(v);

/** `cc-opus` / `cc/opus` → `claude-code/opus`; a full `claude-code/opus` is returned as is. Else null. */
export function expandVendor(token: string, vendors: Record<string, string> = VENDOR_SHORTHAND): string | null {
    const v = token.trim().toLowerCase();
    const m = v.match(/^([a-z0-9]+)[-/](.+)$/);
    if (m && vendors[m[1]]) return `${vendors[m[1]]}/${m[2]}`;
    const slash = v.indexOf('/');
    return slash > 0 && Object.values(vendors).includes(v.slice(0, slash)) ? v : null;
}

/** Resolve one model token: vendor shorthand → catalog exact id; else alias table + catalog in the configured order. */
export function resolveModelToken(token: string, o?: Partial<DirectiveOptions>): ModelHit | { ambiguous: string[] } | null {
    const p = opt(o);
    const v = token.trim().toLowerCase();
    const vendored = expandVendor(v, p.vendors);
    if (vendored) return p.resolveModel ? p.resolveModel(vendored) : p.catalogOnly ? null : { model: vendored }; // no catalog: trust the explicit id
    if (p.catalogOnly) return v.includes('/') || p.aliases[v] || /\d/.test(v) ? p.resolveModel?.(v) ?? null : null;
    const alias = p.aliases[v] ? { model: p.aliases[v] } : null;
    if (!p.resolverFirst) return alias ?? p.resolveModel?.(v) ?? null;
    const hit = p.resolveModel?.(v) ?? null;
    return hit && 'model' in hit ? hit : alias ?? hit;
}

const THINK = ['think', 'adaptive', 'nothink', 'nothinking'];

/** Does a bracket group look like directives (vs. prompt text that happens to start with `[`)? */
export function isDirectiveGroup(raw: string, o?: Partial<DirectiveOptions>): boolean {
    const p = opt(o);
    const tokens = raw.split(',').map((t) => t.trim()).filter(Boolean);
    if (!tokens.length) return false;
    return tokens.every((t) => {
        const i = t.indexOf(':');
        if (i !== -1) return p.keys.includes(t.slice(0, i).trim().toLowerCase());
        const v = t.toLowerCase();
        const pair = splitModePair(v);
        if (pair) return p.keys.includes(pair.op === '->' ? 'delegate' : 'advisor') && ((!!pair.left && isModel(pair.left, p)) || isModel(pair.right, p)); // one resolved side makes it explicit: the other one's miss is reported
        if (v === 'off') return p.keys.includes('mode');
        return !!resolveModelToken(v, p) || (p.catalogOnly && !!p.aliases[v])
            || (p.keys.includes('turns') && /^\d+$/.test(v)) || (p.keys.includes('thinking') && THINK.includes(v));
    });
}

/** Splits the leading `[..]` directive groups off a prompt — EVERY consecutive leading group. */
export function splitDirectiveGroups(text: string, o?: Partial<DirectiveOptions>): { groups: string[]; body: string } {
    const p = opt(o);
    let rest = text;
    const groups: string[] = [];
    for (;;) {
        const m = rest.match(DIRECTIVE_RE);
        if (!m) break;
        const g = m[1].trim();
        if ((groups.length || p.strict) && g && !isDirectiveGroup(g, p)) break;
        groups.push(g);
        rest = m[2];
    }
    return { groups, body: rest };
}

export function parseDirectives(text: string, o?: Partial<DirectiveOptions>): ParsedPrompt {
    const p = opt(o);
    const { groups, body } = splitDirectiveGroups(text, p);
    if (!groups.length) return { prompt: text, directives: {} };
    const prompt = body.trim();
    const raw = groups.filter(Boolean).join(',');
    if (!raw) return { prompt, directives: {} };

    const d: Directives = {};
    const out: ParsedPrompt = { prompt, directives: d };
    const tokens = raw.split(',');
    // A keyed sibling proves the group is directives, so an unresolvable leading model there IS a dropped selection.
    const proven = tokens.some((t) => { const i = t.indexOf(':'); return i !== -1 && p.keys.includes(t.slice(0, i).trim().toLowerCase()); });
    const fail = (tok: string, hit: ReturnType<typeof resolveModelToken>) => {
        out.unresolvedModel ??= tok;
        if (hit && 'ambiguous' in hit) out.ambiguous ??= hit.ambiguous;
    };
    const setModel = (val: string): boolean | ReturnType<typeof resolveModelToken> => {
        const hit = resolveModelToken(val, p);
        if (!hit || !('model' in hit)) return hit;
        d.model = hit.model;
        if (hit.engine && !d.engine) d.engine = hit.engine; // an engine-owned model implies its engine, never over `[engine:x]`
        return true;
    };

    let pos = 0;
    for (const token of tokens) {
        const t = token.trim();
        if (!t) continue;
        const i = t.indexOf(':');
        if (i !== -1) {
            const key = t.slice(0, i).trim().toLowerCase(), val = t.slice(i + 1).trim().toLowerCase();
            if (!p.keys.includes(key)) { p.warn(`[directives] Unknown directive key: "${key}"`); continue; }
            if (key === 'delegate' || key === 'advisor') {
                const hit = resolveModelToken(val, p);
                if (hit && 'model' in hit) d[key] = hit.model;
                else if (!hit && isQualifiedModel(val) && !p.catalogOnly) d[key] = val;
                else fail(val, hit);
                continue;
            }
            if (key === 'model') {
                const r = setModel(val);
                if (r === true) continue;
                // A plain `provider/model` with no catalog to check is concrete — the engine owns the verdict.
                if (!r && isQualifiedModel(val) && !p.catalogOnly) { d.model = val; continue; }
                fail(val, r as any);
            } else applyKey(d, key, val, p.warn);
            continue;
        }
        const val = t.toLowerCase();
        const pair = splitModePair(val);
        if (pair && p.keys.includes(pair.op === '->' ? 'delegate' : 'advisor')) {
            const lhit = pair.left ? resolveModelToken(pair.left, p) : null, rhit = resolveModelToken(pair.right, p);
            const lok = !pair.left || (!!lhit && 'model' in lhit), rok = !!rhit && 'model' in rhit;
            // Explicit once either side resolves: the other side's miss is a dropped selection, never a silent fallback.
            if (lok && rok) {
                if (pair.left) setModel(pair.left);
                if (pair.op === '->') d.delegate = (rhit as ModelHit).model; else d.advisor = (rhit as ModelHit).model;
            } else if (lok || rok) { if (!lok) fail(pair.left, lhit); else fail(pair.right, rhit); }
            else p.warn(`[directives] Unknown models in "${t}"`);
            pos++;
            continue;
        }
        if (val === 'off' && p.keys.includes('mode')) { d.modeOff = true; pos++; continue; }
        const r = pos === 0 ? setModel(val) : null;
        if (r === true) { /* model */ }
        else if (pos <= 1 && /^\d+$/.test(val) && p.keys.includes('turns')) d.turns = parseInt(val, 10);
        else if (['think', 'adaptive'].includes(val) && p.keys.includes('thinking')) d.thinking = 'adaptive';
        else if (['nothink', 'nothinking'].includes(val) && p.keys.includes('thinking')) d.thinking = 'disabled';
        else if (pos === 0) {
            // Ambiguous tokens (and, catalog-only, a known alias the catalog lacks) are model selections; a plain unknown one
            // only with a keyed sibling. An unresolved vendor form (`[cc-by]`) is not a selection.
            if (r || (p.catalogOnly && p.aliases[val]) || (proven && !isQualifiedModel(val))) fail(t, r as any);
            p.warn(`[directives] Unknown model: "${t}"`);
        }
        pos++;
    }
    return out;
}

function applyKey(d: Directives, key: string, val: string, warn: (m: string) => void): void {
    switch (key) {
        case 'turns': if (/^\d+$/.test(val)) d.turns = parseInt(val, 10); else warn(`[directives] Invalid turns value: "${val}"`); break;
        case 'thinking':
        case 'think': if (['adaptive', 'enabled', 'disabled'].includes(val)) d.thinking = val as Directives['thinking']; else warn(`[directives] Invalid thinking value: "${val}"`); break;
        case 'effort': if (['low', 'medium', 'high', 'max'].includes(val)) d.effort = val as Directives['effort']; else warn(`[directives] Invalid effort value: "${val}"`); break;
        case 'engine': d.engine = val; break;
        case 'mode': if (val === 'off') d.modeOff = true; else warn(`[directives] Invalid mode value: "${val}"`); break;
        case 'resume':
            if (['on', 'true', '1'].includes(val)) d.resume = true;
            else if (['off', 'false', '0'].includes(val)) d.resume = false;
            else warn(`[directives] Invalid resume value: "${val}"`);
            break;
    }
}

/** Engine/provider → the model ids it advertises. */
export interface EngineModels { name: string; models: { value: string }[] }

/**
 * A resolver over host catalogs: exact id first, then the bare suffix after the provider prefix —
 * ONLY when unambiguous across engines (`{ ambiguous }` otherwise, never the first match).
 */
export function makeModelResolver(engines: () => EngineModels[]): ModelResolver {
    return (token) => {
        const t = token.toLowerCase();
        const hits: ModelHit[] = [];
        for (const { name, models } of engines()) {
            for (const m of models) {
                if (!m.value) continue;
                const v = m.value.toLowerCase();
                if (v === t) return { model: m.value, engine: name };
                if (v.slice(v.lastIndexOf('/') + 1) === t && !hits.some((h) => h.model === m.value)) hits.push({ model: m.value, engine: name });
            }
        }
        if (hits.length === 1) return hits[0];
        return hits.length > 1 ? { ambiguous: hits.map((h) => h.model) } : null;
    };
}
